import type { PostingDay } from '@/types/brand'
import { POSTING_DAYS } from '@/types/brand'

/** Wall-clock `HH:mm` publishing time per weekday; only posting days carry one. */
export type PublishingTimes = Partial<Record<PostingDay, string>>

/** A posting week as one value: the days a brand posts on and the time on each. */
export interface PublishingWeek {
  readonly days: readonly PostingDay[]
  readonly times: PublishingTimes
}

/** The most posting days a week takes; the server refuses a cadence past it. */
export const POSTS_PER_WEEK_MAX = 5

/** Prefilled the moment a day is switched on, so a new posting day is never timeless. */
export const DEFAULT_PUBLISHING_TIME = '09:00'

const HH_MM_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

/** True when `value` is a well-formed 24-hour `HH:mm` string. */
export function isPublishingTime(value: unknown): value is string {
  return typeof value === 'string' && HH_MM_PATTERN.test(value)
}

/** Keeps the per-day times in step with the selected days: each selected day gets a valid time, others are dropped. */
export function normalizePublishingTimes(
  days: readonly PostingDay[],
  times: PublishingTimes | undefined,
): PublishingTimes {
  const next: PublishingTimes = {}
  for (const day of days) {
    const existing = times?.[day]
    next[day] = isPublishingTime(existing) ? existing : DEFAULT_PUBLISHING_TIME
  }
  return next
}

/** Turns a day on or off, keeping its time in step: switching on prefills, switching off clears. */
export function toggleDay(
  day: PostingDay,
  days: readonly PostingDay[],
  times: PublishingTimes | undefined,
): { days: PostingDay[]; times: PublishingTimes } {
  const selected = new Set(days)
  if (selected.has(day)) selected.delete(day)
  else selected.add(day)

  const nextDays = POSTING_DAYS.filter((candidate) => selected.has(candidate))
  return { days: nextDays, times: normalizePublishingTimes(nextDays, times) }
}

/** Writes one time into every posting day; non-posting days are untouched. */
export function applyTimeToEveryDay(
  time: string,
  days: readonly PostingDay[],
  times: PublishingTimes | undefined,
): PublishingTimes {
  if (!isPublishingTime(time)) return normalizePublishingTimes(days, times)

  const next = normalizePublishingTimes(days, times)
  for (const day of days) next[day] = time
  return next
}

/** The brand's IANA time zone as the browser reports it, for a brand that has never set one. */
export function detectTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

/** True when `day` may be switched: always off, and on only while the week is below {@link POSTS_PER_WEEK_MAX}. */
export function canToggleDay(day: PostingDay, days: readonly PostingDay[]): boolean {
  return days.includes(day) || days.length < POSTS_PER_WEEK_MAX
}

/** True when the week can be saved: at least one posting day, at most {@link POSTS_PER_WEEK_MAX}. */
export function isSavableWeek(week: PublishingWeek): boolean {
  return week.days.length > 0 && week.days.length <= POSTS_PER_WEEK_MAX
}

/** True when two weeks post on the same days at the same times, whatever order they list them in. */
export function sameWeek(a: PublishingWeek, b: PublishingWeek): boolean {
  return POSTING_DAYS.every(
    (day) => a.days.includes(day) === b.days.includes(day) && a.times[day] === b.times[day],
  )
}
